import { useMemo, useState, useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, InputLabel, LinearProgress, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

import {
    calculationIdAtom,
    copyFromConfigAtom,
    destinationIdAtom,
    lotIdAtom,
    phaseIdAtom,
    projectIdAtom,
    unitIdAtom
} from "atoms/Rtool/copyFromConfigAtoms";
import { copyToConfigAtom } from "atoms/Rtool/copyToConfigAtoms";
import type { Config } from "atoms/Rtool/newConfigAtoms";
import { areConfigsEqual } from "utils/utils";
import { ProjectSelect } from "utils/projectSelector/ProjectSelect";
import { DestinationSelect } from "utils/projectSelector/DestinationSelect";
import { LotSelect } from "utils/projectSelector/LotSelect";
import { PhaseSelect } from "utils/projectSelector/PhaseSelect";
import { UnitSelect } from "utils/projectSelector/UnitSelect";
import { CalculationSelect } from "utils/projectSelector/CalculationSelect";
import { isConfigComplete } from "utils/utils";
import { errorAtom } from "atoms/error";
import { useSnackbar } from 'utils/hooks/useSnackbar';
import { toUserMessage } from 'utils/apiErrorMessage';
import { authTokenAtom } from "atoms/authAtoms";
import { powertrainAtom } from "atoms/powertrainAtom";
import { fetchGears } from "api/fetchRtool";
import {
    submitAllCopyProject, submitSubCopyProject, submitAllCopyProjectHev, submitSubCopyProjectHev,
    submitFullCopy, submitCopyCommon, submitCopyConditions, submitCopyConditionsHev, type CopyFailedItem,
} from "api/submitRtool";
import { fetchLatestCopyJob, isTerminalCopyJobStatus, type CopyJobSnapshot } from "api/copyJobs";
import { startCopyJobAtom, beginWatchingCopyJobAtom } from "atoms/Rtool/copyJobAtoms";
import { useCopyJobEntry } from "utils/hooks/useCopyJobBlocking";
import { buildCopyJobProgressCaption, buildFailedJobMessage } from "utils/copyJobProgress";
import { DRIVE_MODE_IDS, PHENOMENON_IDS, CONDITION_COPY_DATASET_IDS } from "utils/consts";
import { DRIVE_MODE_IDS as HEV_DRIVE_MODE_IDS, PHENOMENON_IDS as HEV_PHENOMENON_IDS, GEAR_IDS } from "utils/constsHev";
import { mappingAtom } from "atoms/mappingAtoms";
import { buildLabeledOptions } from "utils/mappingLabel";
import { buildConditionLabel } from "utils/rCalcConditionResult";
import { naturalSort } from "utils/sort";
import {
    FailedItemsList, buildCopyErrorMessage, classifySummary, buildResultMessage,
    extractAcousticMismatchIds, type BatchSummary,
} from "components/FailedItemsList";

// ダイアログが開いた時点でコピー元 calc の登録済み gear を取得する（コピー元 gear 選択肢）。
// 条件単位コピーダイアログと複数条件コピーダイアログ（#36）の両方で使うため切り出す。
const useCopySourceGears = (
    open: boolean,
    isHev: boolean,
    authToken: string,
    fromConfig: Config,
    resetGearSelection: () => void,
) => {
    const [fromGears, setFromGears] = useState<string[]>([]);
    // コピー元 gear の取得状態。0 件提示は 'success' かつ空のときのみ行い、取得中/取得失敗とは区別する。
    const [fromGearsStatus, setFromGearsStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

    useEffect(() => {
        if (!open || !isHev) {
            setFromGearsStatus('idle');
            return;
        }
        let active = true; // close/reopen 前の古い結果が現在の状態を上書きしないようにする
        setFromGears([]);
        resetGearSelection();
        setFromGearsStatus('loading');
        fetchGears(authToken, fromConfig)
            .then((gears) => {
                if (!active) return;
                setFromGears(gears);
                setFromGearsStatus('success');
            })
            .catch(() => {
                if (!active) return;
                setFromGears([]);
                setFromGearsStatus('error');
            });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isHev]);

    return { fromGears, fromGearsStatus };
};

type CopyGearSelectorsProps = {
    idPrefix: string;
    gearIds: readonly string[];
    destGearId: string;
    onDestGearIdChange: (value: string) => void;
    srcGearId: string;
    onSrcGearIdChange: (value: string) => void;
    fromGears: string[];
    fromGearsStatus: 'idle' | 'loading' | 'success' | 'error';
};

// HEV: コピー先 gear（URL）/ コピー元 gear（body.gear_id）の選択UI。
// 条件単位コピーダイアログと複数条件コピーダイアログ（#36）の両方で使うため切り出す。
const CopyGearSelectors: React.FC<CopyGearSelectorsProps> = ({
    idPrefix, gearIds, destGearId, onDestGearIdChange, srcGearId, onSrcGearIdChange, fromGears, fromGearsStatus,
}) => (
    <>
        <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id={`${idPrefix}-dest-gear-label`}>コピー先 gear</InputLabel>
            <Select
                labelId={`${idPrefix}-dest-gear-label`}
                label="コピー先 gear"
                value={destGearId}
                onChange={(e) => onDestGearIdChange(e.target.value as string)}
            >
                {gearIds.map((id) => (
                    <MenuItem key={id} value={id}>{id}</MenuItem>
                ))}
            </Select>
        </FormControl>
        <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id={`${idPrefix}-src-gear-label`}>コピー元 gear</InputLabel>
            <Select
                labelId={`${idPrefix}-src-gear-label`}
                label="コピー元 gear"
                value={srcGearId}
                onChange={(e) => onSrcGearIdChange(e.target.value as string)}
            >
                {gearIds.filter((g) => fromGears.includes(g)).map((id) => (
                    <MenuItem key={id} value={id}>{id}</MenuItem>
                ))}
            </Select>
        </FormControl>
        {fromGearsStatus === 'success' && fromGears.length === 0 && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                コピー元に登録済みの Gear がありません。
            </Typography>
        )}
        {fromGearsStatus === 'error' && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                コピー元 Gear の取得に失敗しました。時間をおいて再度お試しください。
            </Typography>
        )}
    </>
);

type CopyJobResultDetailProps = {
    snapshot: CopyJobSnapshot;
};

// terminal（completed/failed）に達したジョブの結果表示（issue #45）。completedは条件単位コピーの
// 結果表示と同じヘルパー（classifySummary/buildResultMessage/FailedItemsList）をそのまま流用する
// （issue本文: message/summary/failed_itemsはバイト単位で同一形式のため流用できる）。failedは
// messageを参照せずbuildFailedJobMessageで文言を組む（issue #45 §3の要件）。
const CopyJobResultDetail: React.FC<CopyJobResultDetailProps> = ({ snapshot }) => {
    if (snapshot.status === 'completed') {
        const isSuccess = classifySummary(snapshot.summary) === 'all_success';
        return (
            <>
                <Typography variant="body2" color={isSuccess ? 'success.main' : 'error'}>
                    {buildResultMessage(snapshot.summary)}
                </Typography>
                <FailedItemsList items={snapshot.failed_items ?? []} />
            </>
        );
    }
    return (
        <>
            <Typography variant="body2" color="error">
                {buildFailedJobMessage(snapshot)}
            </Typography>
            <FailedItemsList items={snapshot.failed_items ?? []} />
        </>
    );
};

export const ProjectCopy: React.FC = () => {
    const authToken = useAtomValue(authTokenAtom);
    const config = useAtomValue(copyToConfigAtom);
    const fromConfig = useAtomValue(copyFromConfigAtom);
    const mapping = useAtomValue(mappingAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const setError = useSetAtom(errorAtom);
    const { showSuccess, showError } = useSnackbar();
    const startCopyJob = useSetAtom(startCopyJobAtom);
    const beginWatchingCopyJob = useSetAtom(beginWatchingCopyJobAtom);
    // /code-review所見: useIsCopyJobBlockingは内部でuseCopyJobEntryと同じcopyJobsAtom購読・
    // buildDestinationKey計算を行うため、ここではcopyJobEntryから直接導出し二重購読を避ける。
    const copyJobEntry = useCopyJobEntry(powertrain, config);
    const isCopyBlocking = copyJobEntry !== undefined && copyJobEntry.watchState !== 'terminal';
    // 「前回のコピー結果があります」バナー（resultOrigin==='restored'）の展開状態。issue #45 §4.1:
    // 自動で開かず導線のみ出す。ジョブが変わったら（新しいコピー開始等）折りたたみ直す。
    const [resultExpanded, setResultExpanded] = useState(false);
    useEffect(() => {
        setResultExpanded(false);
    }, [copyJobEntry?.jobId]);

    const isHev = powertrain === 'hev';
    const driveModeIds = isHev ? HEV_DRIVE_MODE_IDS : DRIVE_MODE_IDS;
    const phenomenonIds = isHev ? HEV_PHENOMENON_IDS : PHENOMENON_IDS;

    // 現象・運転モードは定義順で表示する（各条件表示・マトリクスの軸順と一致させる）
    const driveModeOptions = useMemo(
        () => buildLabeledOptions(mapping, 'drive_mode_id', driveModeIds),
        [mapping, driveModeIds],
    );
    const phenomenonOptions = useMemo(
        () => buildLabeledOptions(mapping, 'phenomenon_id', phenomenonIds),
        [mapping, phenomenonIds],
    );
    const datasetIds = useMemo(() => naturalSort(CONDITION_COPY_DATASET_IDS), []);
    const gearIds = useMemo(() => [...GEAR_IDS], []);
    // 複数条件コピー（#36）の条件選択候補。音響性による事前フィルタは行わない（判定はBE側）。
    const conditionOptions = useMemo(
        () => phenomenonIds.flatMap((p) => driveModeIds.map((d) => {
            const value = `${p}-${d}`;
            return { value, label: buildConditionLabel(value, mapping) };
        })),
        [phenomenonIds, driveModeIds, mapping],
    );

    const [loading, setLoading] = useState(false);
    const [copyOpen, setCopyOpen] = useState(false);
    const [copyMode, setCopyMode] = useState<"all" | "sub">("sub");
    const [fullCopyOpen, setFullCopyOpen] = useState(false);
    const [conditionsCopyOpen, setConditionsCopyOpen] = useState(false);

    const [drive_mode_id, setDriveModeId] = useState<string>("");
    const [phenomenon_id, setPhenomenonId] = useState<string>("");
    const [dataset_id, setDatasetId] = useState<string>("");
    const [destGearId, setDestGearId] = useState<string>("");
    const [srcGearId, setSrcGearId] = useState<string>("");
    const [failedItems, setFailedItems] = useState<CopyFailedItem[]>([]);
    const [copyMessage, setCopyMessage] = useState<string | null>(null);
    // 丸ごとコピー用/複数条件コピー用/commonコピー用ダイアログは条件単位コピーダイアログと state を共有しない
    // （いずれも onClose が loading 中でも発火するため、共有するとダイアログ間で結果表示が混線する）。
    const [fullCopyFailedItems, setFullCopyFailedItems] = useState<CopyFailedItem[]>([]);
    const [fullCopyMessage, setFullCopyMessage] = useState<string | null>(null);

    const [commonCopyOpen, setCommonCopyOpen] = useState(false);
    const [commonCopyFailedItems, setCommonCopyFailedItems] = useState<CopyFailedItem[]>([]);
    const [commonCopyMessage, setCommonCopyMessage] = useState<string | null>(null);

    const [sourceConditionId, setSourceConditionId] = useState<string>("");
    const [destinationConditionIds, setDestinationConditionIds] = useState<string[]>([]);
    const [ccDestGearId, setCcDestGearId] = useState<string>("");
    const [ccSrcGearId, setCcSrcGearId] = useState<string>("");
    const [conditionsCopyFailedItems, setConditionsCopyFailedItems] = useState<CopyFailedItem[]>([]);
    const [conditionsCopyMessage, setConditionsCopyMessage] = useState<string | null>(null);
    // 音響性不一致(400)の不一致宛先一覧。除外操作の対象を保持する（issue #36）。
    const [conditionsCopyMismatch, setConditionsCopyMismatch] = useState<{ ids: string[]; remainingCount: number } | null>(null);

    // 条件単位コピーダイアログと複数条件コピーダイアログは同じ fromConfig のコピー元 gear 一覧を参照するため、
    // 一方が開いている間は共有の1回だけ取得する（別々に取得すると同じ内容を二重にfetchしてしまう）。
    const { fromGears, fromGearsStatus } = useCopySourceGears(
        copyOpen || conditionsCopyOpen, isHev, authToken, fromConfig, () => {
            setSrcGearId("");
            setDestGearId("");
            setCcSrcGearId("");
            setCcDestGearId("");
        },
    );

    const buildConfigPath = (c: Config) =>
        [c.project_id, c.destination_id, c.lot_id, c.phase_id, c.unit_id, c.calculation_id]
            .filter(id => id)
            .join(' / ');

    const selectedPath = buildConfigPath(fromConfig);
    const destinationPath = buildConfigPath(config);

    const sameAsTarget = areConfigsEqual(config, fromConfig);
    // 5つのコピー実行ボタンで共通のdisabled条件（コピー元/先の未確定・同一・実行中・
    // このコピー先で他のコピージョブが実行中=isCopyBlocking。issue #43 §2.5）。
    const copyActionDisabled = !isConfigComplete(config) || !isConfigComplete(fromConfig) || sameAsTarget
        || loading || isCopyBlocking;

    const handleDialogClose = () => {
        setCopyOpen(false);
        setFailedItems([]);
        setCopyMessage(null);
        setDestGearId("");
        setSrcGearId("");
    };

    const handleFullCopyDialogClose = () => {
        setFullCopyOpen(false);
        setFullCopyFailedItems([]);
        setFullCopyMessage(null);
    };

    // copy系APIレスポンスの成否判定・表示ロジック（400以上/summary欠落/4分類）は
    // 条件単位コピーと丸ごとコピーで共通のため、setter/成功時処理のみ差し替えて共有する。
    const applyCopyResponse = (
        response: { status: number; message?: string; summary?: BatchSummary; failed_items?: CopyFailedItem[] },
        setMessage: (msg: string | null) => void,
        setItems: (items: CopyFailedItem[]) => void,
        onSuccess: () => void,
    ) => {
        if (response.status >= 400) {
            setMessage(buildCopyErrorMessage(response));
            setItems(response.failed_items ?? []);
            return;
        }
        const failed = response.failed_items ?? [];
        if (!response.summary) {
            // summary 欠落（想定外）。failed_items の有無で簡易判定。
            if (failed.length > 0) {
                setMessage("一部の登録に失敗しました。");
                setItems(failed);
                return;
            }
            showSuccess(response.message || "コピー処理が完了しました。");
        } else if (classifySummary(response.summary) === 'all_success') {
            showSuccess(buildResultMessage(response.summary));
        } else {
            setMessage(buildResultMessage(response.summary));
            setItems(failed);
            return;
        }
        onSuccess();
    };

    // setLoading/setError/try-catch-finally/showError の骨格は3つの確定ハンドラで共通のため集約する。
    const withCopyLoading = async (action: () => Promise<void>) => {
        setLoading(true);
        setError(null);
        try {
            await action();
        } catch (e) {
            setError(toUserMessage(e));
            showError(toUserMessage(e));
        } finally {
            setLoading(false);
        }
    };

    // loading中はEsc/背景クリックでダイアログを閉じない（応答到着時に結果表示先ごと消えるのを防ぐ）。
    // 丸ごとコピー/複数条件コピー/commonコピーの3ダイアログで共通のガード。
    const closeUnlessLoading = (closeFn: () => void) => () => { if (!loading) closeFn(); };

    const handleConfirm = () => withCopyLoading(async () => {
        setFailedItems([]);
        setCopyMessage(null);
        let response;
        if (isHev) {
            response = copyMode === "all"
                ? await submitAllCopyProjectHev(authToken, config, fromConfig, destGearId, srcGearId, drive_mode_id, phenomenon_id)
                : await submitSubCopyProjectHev(authToken, config, fromConfig, destGearId, srcGearId, drive_mode_id, phenomenon_id, dataset_id);
        } else {
            response = copyMode === "all"
                ? await submitAllCopyProject(authToken, config, fromConfig, drive_mode_id, phenomenon_id)
                : await submitSubCopyProject(authToken, config, fromConfig, drive_mode_id, phenomenon_id, dataset_id);
        }
        applyCopyResponse(response, setCopyMessage, setFailedItems, () => {
            setCopyOpen(false);
            setDriveModeId("");
            setPhenomenonId("");
            setDatasetId("");
        });
    });

    // 計算セット丸ごとコピー（issue #35）。全条件＋common を無条件上書きするため、
    // ドライブ/現象/データセット/gear の選択は不要（成否表示は既存の共通ヘルパーを流用）。
    // issue #43: 応答が200→202化された。job_id の有無で受理(202)を判定し、A1のジョブ監視に
    // 引き渡す。結果を待たないため loading は応答到着時点で解除される（withCopyLoading の骨格通り）。
    const handleFullCopyConfirm = () => withCopyLoading(async () => {
        setFullCopyFailedItems([]);
        setFullCopyMessage(null);
        const response = await submitFullCopy(authToken, config, fromConfig, powertrain);

        if ('job_id' in response) {
            // job_idはキー存在だけでなく値も見る（BEが202を返しつつjob_idが空/nullの不整合応答を
            // 返した場合にwatching状態で永続ロックしないため。/code-review xhigh所見）。
            if (!response.job_id) {
                setFullCopyMessage("コピーの受理応答が不正です。もう一度お試しください。");
                return;
            }
            startCopyJob({
                powertrain, config,
                jobId: response.job_id,
                statusUrl: response.status_url,
                pollIntervalSeconds: response.poll_interval_seconds,
            });
            showSuccess("コピーを開始しました。他の操作に進めます。進捗はこの画面を開くと確認できます。");
            setFullCopyOpen(false);
            return;
        }

        if (response.status === 409) {
            let latest;
            try {
                latest = await fetchLatestCopyJob(authToken, powertrain, config);
            } catch {
                // 409の原因（他コピー実行中）がwithCopyLoadingの汎用エラーに埋もれないよう、
                // ここで専用の文言を出す（/code-review xhigh所見）。
                setFullCopyMessage("このコピー先では既にコピーが実行中ですが、状況の取得に失敗しました。もう一度お試しください。");
                return;
            }
            if (!latest) {
                // 契約上ほぼ起こらない（ジョブの記録は7日残るため）。
                setFullCopyMessage("コピー状況を取得できませんでした。もう一度お試しください。");
                return;
            }
            if (!isTerminalCopyJobStatus(latest.status)) {
                beginWatchingCopyJob({
                    powertrain, config,
                    jobId: latest.job_id,
                    statusUrl: latest.status_url,
                    seedSnapshot: latest,
                });
                showSuccess("このコピー先では既にコピーが実行中です。進捗を表示します。");
                setFullCopyOpen(false);
                return;
            }
            // 409直後にジョブが終端に達していた場合。再試行を促さずその結果を表示する（issue #43 §2.4）。
            // copyJobsAtomにも反映する：これを省くと常設の結果パネル・ヘッダーインジケーター・
            // document.titleがこのジョブを一切知らないままになり、この宛先に古いterminalエントリが
            // 残っていた場合はダイアログの表示（このジョブ）とパネルの表示（古いジョブ）が矛盾して
            // 見える（/code-review xhigh所見）。resultOriginは'live'（このユーザー操作の直接の
            // 結果として今まさに判明したため。issue #45）。
            beginWatchingCopyJob({
                powertrain, config,
                jobId: latest.job_id,
                statusUrl: latest.status_url,
                seedSnapshot: latest,
                terminalResultOrigin: 'live',
            });
            if (latest.status === 'completed') {
                applyCopyResponse(
                    { status: 200, message: latest.message, summary: latest.summary, failed_items: latest.failed_items },
                    setFullCopyMessage, setFullCopyFailedItems, () => setFullCopyOpen(false),
                );
                return;
            }
            // failedはmessageを参照せずbuildFailedJobMessageで文言を組む（issue #45 §3の要件）。
            setFullCopyMessage(buildFailedJobMessage(latest));
            setFullCopyFailedItems(latest.failed_items ?? []);
            return;
        }

        applyCopyResponse(response, setFullCopyMessage, setFullCopyFailedItems, () => setFullCopyOpen(false));
    });

    const handleCommonCopyDialogClose = () => {
        setCommonCopyOpen(false);
        setCommonCopyFailedItems([]);
        setCommonCopyMessage(null);
    };

    // commonコピー（issue #37）。NTF・諸元のみを無条件上書きするため、
    // ドライブ/現象/データセット/gear の選択は不要（成否表示は既存の共通ヘルパーを流用）。
    const handleCommonCopyConfirm = () => withCopyLoading(async () => {
        setCommonCopyFailedItems([]);
        setCommonCopyMessage(null);
        const response = await submitCopyCommon(authToken, config, fromConfig, powertrain);
        applyCopyResponse(response, setCommonCopyMessage, setCommonCopyFailedItems, () => setCommonCopyOpen(false));
    });

    const handleConditionsCopyDialogClose = () => {
        setConditionsCopyOpen(false);
        setConditionsCopyFailedItems([]);
        setConditionsCopyMessage(null);
        setConditionsCopyMismatch(null);
        setCcDestGearId("");
        setCcSrcGearId("");
    };

    // 複数条件コピー（issue #36）。1つのコピー元条件から複数の宛先条件へ一括コピーする。
    // 音響性制約（音響↔振動を跨ぐ宛先の400）はBE側で判定するため、FEは事前フィルタしない。
    const handleConditionsCopyConfirm = () => withCopyLoading(async () => {
        setConditionsCopyFailedItems([]);
        setConditionsCopyMessage(null);
        setConditionsCopyMismatch(null);
        const response = isHev
            ? await submitCopyConditionsHev(authToken, config, fromConfig, ccDestGearId, ccSrcGearId, sourceConditionId, destinationConditionIds)
            : await submitCopyConditions(authToken, config, fromConfig, sourceConditionId, destinationConditionIds);
        setConditionsCopyMismatch(extractAcousticMismatchIds(response.message));
        applyCopyResponse(response, setConditionsCopyMessage, setConditionsCopyFailedItems, () => {
            setConditionsCopyOpen(false);
            setSourceConditionId("");
            setDestinationConditionIds([]);
        });
    });

    // 音響性不一致の宛先をまとめて選択解除する（外して再実行できる導線。自動再送はしない）。
    // BEのmessageは不一致宛先を先頭5件までしか列挙しない（#36契約）ため、remainingCount>0の場合は
    // 除外できていない宛先が残っている可能性がある。その場合は警告を消さず、再確定で残りを確認するよう促す
    // （全件消してしまうと「解決済み」に見えて、残存する不一致で400が再発する原因がユーザーに分からなくなるため）。
    const handleRemoveMismatchedDestinations = () => {
        if (!conditionsCopyMismatch) return;
        const { ids, remainingCount } = conditionsCopyMismatch;
        const mismatchedIds = new Set(ids);
        setDestinationConditionIds((prev) => prev.filter((id) => !mismatchedIds.has(id)));
        setConditionsCopyMismatch(null);
        if (remainingCount > 0) {
            setConditionsCopyMessage(
                `上記の宛先を除外しました。まだ音響性が一致しない宛先が残っている可能性があります（他 ${remainingCount} 件）。再度「確定」を押して確認してください。`,
            );
            return;
        }
        setConditionsCopyMessage(null);
    };

    return (
        <Box p={2}>
            <Typography>他計算セットからデータをコピーします。</Typography>

            {selectedPath ? (
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    選択中のデータ登録元計算セット：{selectedPath}
                </Typography>
            ) : (
                <Typography variant="subtitle1" sx={{ mb: 2, color: 'error.main' }}>
                    データ登録元計算セットが選択されていません。
                </Typography>
            )}

            {selectedPath && sameAsTarget && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    登録元と登録先が同一です。コピー先を変更してください。
                </Alert>
            )}

            <Stack
                component="form"
                spacing={2}
                noValidate
                autoComplete="off"
                direction="column"
                alignItems="stretch"
            >
                <ProjectSelect atom={projectIdAtom} />
                <DestinationSelect atom={destinationIdAtom} config={fromConfig} />
                <LotSelect atom={lotIdAtom} config={fromConfig} />
                <PhaseSelect atom={phaseIdAtom} config={fromConfig} />
                <UnitSelect atom={unitIdAtom} config={fromConfig} />
                <CalculationSelect atom={calculationIdAtom} config={fromConfig} />
            </Stack>

            {/* ★ コピー実行ボタン群。狭い画面幅でボタンがクリップされないようflexWrapする。 */}
            <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: 'wrap' }}>
                <Button
                    variant="contained"
                    sx={{ minWidth: 140 }}
                    disabled={copyActionDisabled}
                    onClick={() => {
                        setCopyMode("all");
                        setCopyOpen(true);
                    }}
                >
                    {loading ? "コピー中..." : "全データコピー"}
                </Button>
                <Button
                    variant="contained"
                    sx={{ minWidth: 140 }}
                    disabled={copyActionDisabled}
                    onClick={() => {
                        setCopyMode("sub");
                        setCopyOpen(true);
                    }}
                >
                    {loading ? "コピー中..." : "個別データコピー"}
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    sx={{ minWidth: 140 }}
                    disabled={copyActionDisabled}
                    onClick={() => {
                        setFullCopyMessage(null);
                        setFullCopyFailedItems([]);
                        setFullCopyOpen(true);
                    }}
                >
                    {loading ? "コピー中..." : "計算セット丸ごとコピー"}
                </Button>
                <Button
                    variant="contained"
                    sx={{ minWidth: 140 }}
                    disabled={copyActionDisabled}
                    onClick={() => {
                        setSourceConditionId("");
                        setDestinationConditionIds([]);
                        setConditionsCopyMessage(null);
                        setConditionsCopyFailedItems([]);
                        setConditionsCopyMismatch(null);
                        setConditionsCopyOpen(true);
                    }}
                >
                    {loading ? "コピー中..." : "複数条件コピー"}
                </Button>
                <Button
                    variant="contained"
                    sx={{ minWidth: 140 }}
                    disabled={copyActionDisabled}
                    onClick={() => {
                        setCommonCopyMessage(null);
                        setCommonCopyFailedItems([]);
                        setCommonCopyOpen(true);
                    }}
                >
                    {loading ? "コピー中..." : "Common コピー（NTF・諸元）"}
                </Button>
            </Stack>

            {isHev && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    条件単位コピー（全データコピー/個別データコピー/複数条件コピー）は HEV では gear を個別に指定できます。BEV↔HEV 間のコピーはできません。
                </Typography>
            )}

            {/* issue #44: task進捗（done_tasks/total_tasks）のみ表示する。tasks[]の個別状態や
                「現在処理中のtask」は出さない（BE契約訂正でtasks[].statusにrunningが来ず判別不能なため）。
                watchState==='error'（監視自体を諦めた状態）では進捗バー・継続案内は出さない
                （/code-review所見: 直前のsnapshotに基づく古い進捗が「動いている」ように見えてしまう）。 */}
            {isCopyBlocking && copyJobEntry && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="warning.main">
                        コピー実行中のため操作できません。
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                        {copyJobEntry.watchState === 'watching' && <CircularProgress size={16} />}
                        <Typography variant="body2" color="text.secondary">
                            {buildCopyJobProgressCaption(copyJobEntry)}
                        </Typography>
                    </Stack>
                    {copyJobEntry.watchState !== 'error' && copyJobEntry.snapshot && (
                        <>
                            <LinearProgress
                                aria-label="コピー進捗"
                                variant="determinate"
                                value={
                                    copyJobEntry.snapshot.progress.total_tasks > 0
                                        ? (copyJobEntry.snapshot.progress.done_tasks / copyJobEntry.snapshot.progress.total_tasks) * 100
                                        : 0
                                }
                                sx={{ mt: 0.5 }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                数分かかる場合があります。画面を離れても処理は続きます。進捗が止まって見える場合がありますが、処理は継続しています。
                            </Typography>
                        </>
                    )}
                </Box>
            )}

            {/* issue #45: terminal（completed/failed）到達時の結果表示。resultOrigin==='live'
                （このセッションで監視していたループ自身がたった今検知した遷移）は即時インライン表示
                （§4経路1: 進捗表示がそのまま結果表示に切り替わる）。resultOrigin==='restored'
                （jobs/latestのcold restore＝前回の結果）は自動で開かず、導線バナーのみ出す
                （§4.1: 開くたびに古い完了を再表示しない）。 */}
            {copyJobEntry?.watchState === 'terminal' && copyJobEntry.snapshot && (
                <Box sx={{ mt: 1 }}>
                    {copyJobEntry.resultOrigin === 'restored' && !resultExpanded ? (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                <Typography variant="body2">前回のコピー結果があります。</Typography>
                                <Button size="small" onClick={() => setResultExpanded(true)}>
                                    詳細を見る
                                </Button>
                            </Stack>
                        </Alert>
                    ) : (
                        <CopyJobResultDetail snapshot={copyJobEntry.snapshot} />
                    )}
                </Box>
            )}

            {/* ★ コピー用ダイアログ */}
            <Dialog open={copyOpen} onClose={handleDialogClose} fullWidth maxWidth="xs">
                <DialogTitle>
                    {copyMode === "all"
                        ? "ドライブモードIDと現象IDを選択してください。"
                        : "ドライブモードIDと現象IDとデータセットIDを選択してください"}
                </DialogTitle>
                <DialogContent>

                    {/* ドライブID */}
                    <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel id="drive-select-label">ドライブモードID</InputLabel>
                        <Select
                            labelId="drive-select-label"
                            label="ドライブID"
                            value={drive_mode_id}
                            onChange={(e) => setDriveModeId(e.target.value as string)}
                        >
                            {driveModeOptions.map(({ value, label }) => (
                                <MenuItem key={value} value={value}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* 現象ID */}
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel id="phenomenon-select-label">現象ID</InputLabel>
                        <Select
                            labelId="phenomenon-select-label"
                            label="現象ID"
                            value={phenomenon_id}
                            onChange={(e) => setPhenomenonId(e.target.value as string)}
                        >
                            {phenomenonOptions.map(({ value, label }) => (
                                <MenuItem key={value} value={value}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* データセットID */}
                    {copyMode === "sub" && (
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel id="dataset-select-label">データセットID</InputLabel>
                            <Select
                                labelId="dataset-select-label"
                                value={dataset_id}
                                onChange={(e) => setDatasetId(e.target.value as string)}
                            >
                                {datasetIds.map((id) => (
                                    <MenuItem key={id} value={id}>
                                        {id}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* HEV: コピー先 gear（URL）/ コピー元 gear（body.config.gear_id） */}
                    {isHev && (
                        <CopyGearSelectors
                            idPrefix="copy"
                            gearIds={gearIds}
                            destGearId={destGearId}
                            onDestGearIdChange={setDestGearId}
                            srcGearId={srcGearId}
                            onSrcGearIdChange={setSrcGearId}
                            fromGears={fromGears}
                            fromGearsStatus={fromGearsStatus}
                        />
                    )}

                    {copyMessage && (
                        <Box sx={{ mt: 2 }}>
                            <Typography color="error" variant="body2">
                                {copyMessage}
                            </Typography>
                            <FailedItemsList items={failedItems} />
                        </Box>
                    )}

                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>キャンセル</Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={
                            !drive_mode_id ||
                            !phenomenon_id ||
                            (copyMode === "sub" && !dataset_id) ||
                            (isHev && (!destGearId || !srcGearId)) ||
                            loading ||
                            isCopyBlocking
                        }
                        startIcon={loading ? <CircularProgress size={18} /> : undefined}
                    >
                        {loading ? "コピー中..." : "確定"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ★ 丸ごとコピー確認ダイアログ（issue #35）。無条件上書きのため強い警告を表示する。
                実行中（loading）はEsc/背景クリックでも閉じない：閉じてしまうと応答到着時に
                結果表示先ごと消え、無条件上書き操作の失敗が無通知になるため。 */}
            <Dialog
                open={fullCopyOpen}
                onClose={closeUnlessLoading(handleFullCopyDialogClose)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>計算セット丸ごとコピーの確認</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        コピー先の計算セット全体（全条件＋common）を、コピー元の内容で無条件に上書きします。この操作は元に戻せません。実行しますか？
                    </DialogContentText>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        コピー元：{selectedPath}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        コピー先：{destinationPath}
                    </Typography>

                    {fullCopyMessage && (
                        <Box sx={{ mt: 2 }}>
                            <Typography color="error" variant="body2">
                                {fullCopyMessage}
                            </Typography>
                            <FailedItemsList items={fullCopyFailedItems} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleFullCopyDialogClose} disabled={loading}>キャンセル</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleFullCopyConfirm}
                        disabled={loading || isCopyBlocking}
                        startIcon={loading ? <CircularProgress size={18} /> : undefined}
                    >
                        {loading ? "コピー中..." : "実行"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ★ 複数条件コピーダイアログ（issue #36）。宛先condition複数選択・
                音響性不一致(400)時は不一致宛先一覧を表示し除外操作を提供する（判定はBE側、FEは判定しない）。
                実行中（loading）はEsc/背景クリックでも閉じない：閉じてしまうと応答到着時に
                結果表示先ごと消え、複数宛先への書き込み結果の一部（400/部分失敗）が無通知になるため。 */}
            <Dialog
                open={conditionsCopyOpen}
                onClose={closeUnlessLoading(handleConditionsCopyDialogClose)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>コピー元条件と宛先条件（複数）を選択してください。</DialogTitle>
                <DialogContent>

                    <Autocomplete
                        options={conditionOptions}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        value={conditionOptions.find((o) => o.value === sourceConditionId) ?? null}
                        onChange={(_, newValue) => setSourceConditionId(newValue?.value ?? "")}
                        renderInput={(params) => <TextField {...params} label="コピー元 条件" />}
                        sx={{ mt: 1 }}
                    />

                    {isHev && (
                        <CopyGearSelectors
                            idPrefix="cc"
                            gearIds={gearIds}
                            destGearId={ccDestGearId}
                            onDestGearIdChange={setCcDestGearId}
                            srcGearId={ccSrcGearId}
                            onSrcGearIdChange={setCcSrcGearId}
                            fromGears={fromGears}
                            fromGearsStatus={fromGearsStatus}
                        />
                    )}

                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={conditionOptions}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        value={conditionOptions.filter((o) => destinationConditionIds.includes(o.value))}
                        onChange={(_, newValue) => setDestinationConditionIds(newValue.map((o) => o.value))}
                        renderOption={(props, option, { selected }) => {
                            const { key, ...optionProps } = props;
                            return (
                                <li key={key} {...optionProps} style={{ fontWeight: selected ? 700 : 400 }}>
                                    <Checkbox
                                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                                        checked={selected}
                                        sx={{ mr: 1 }}
                                    />
                                    {option.label}
                                </li>
                            );
                        }}
                        renderInput={(params) => <TextField {...params} label="宛先 条件（複数選択可）" />}
                        sx={{ mt: 2 }}
                    />

                    {conditionsCopyMismatch && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                音響性が一致しない宛先があります。除外して再実行してください。
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, mb: 1 }}>
                                {conditionsCopyMismatch.ids.map((id) => (
                                    <Typography component="li" variant="body2" key={id}>
                                        {buildConditionLabel(id, mapping)}
                                    </Typography>
                                ))}
                            </Box>
                            {conditionsCopyMismatch.remainingCount > 0 && (
                                <Typography variant="body2">
                                    他 {conditionsCopyMismatch.remainingCount} 件
                                </Typography>
                            )}
                            <Button size="small" onClick={handleRemoveMismatchedDestinations} sx={{ mt: 1 }}>
                                上記の宛先を除外
                            </Button>
                        </Alert>
                    )}

                    {conditionsCopyMessage && (
                        <Box sx={{ mt: 2 }}>
                            <Typography color="error" variant="body2">
                                {conditionsCopyMessage}
                            </Typography>
                            <FailedItemsList items={conditionsCopyFailedItems} />
                        </Box>
                    )}

                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConditionsCopyDialogClose} disabled={loading}>キャンセル</Button>
                    <Button
                        variant="contained"
                        onClick={handleConditionsCopyConfirm}
                        disabled={
                            !sourceConditionId ||
                            destinationConditionIds.length === 0 ||
                            (isHev && (!ccDestGearId || !ccSrcGearId)) ||
                            loading ||
                            isCopyBlocking
                        }
                        startIcon={loading ? <CircularProgress size={18} /> : undefined}
                    >
                        {loading ? "コピー中..." : "確定"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ★ commonコピー確認ダイアログ（issue #37）。影響範囲がNTF・諸元に限定されるため
                #35丸ごとコピーより弱い警告とする（color="error"は付けない）。
                実行中（loading）はEsc/背景クリックでも閉じない：閉じてしまうと応答到着時に
                結果表示先ごと消え、上書き操作の失敗が無通知になるため。 */}
            <Dialog
                open={commonCopyOpen}
                onClose={closeUnlessLoading(handleCommonCopyDialogClose)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Common コピー（NTF・諸元）の確認</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        コピー先のNTFを丸ごと置き換え、諸元データは既存の値に上書きマージします。
                        条件・ギアごとのデータ（振動・音響など）は変更されません。この操作は元に戻せません。実行しますか？
                    </DialogContentText>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        コピー元：{selectedPath}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        コピー先：{destinationPath}
                    </Typography>

                    {commonCopyMessage && (
                        <Box sx={{ mt: 2 }}>
                            <Typography color="error" variant="body2">
                                {commonCopyMessage}
                            </Typography>
                            <FailedItemsList items={commonCopyFailedItems} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCommonCopyDialogClose} disabled={loading}>キャンセル</Button>
                    <Button
                        variant="contained"
                        onClick={handleCommonCopyConfirm}
                        disabled={loading || isCopyBlocking}
                        startIcon={loading ? <CircularProgress size={18} /> : undefined}
                    >
                        {loading ? "コピー中..." : "実行"}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box >
    );
};
